import React from 'react';
import { motion } from 'framer-motion';

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.98,
  },
  enter: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.98,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Cinematic slide overlay variants
const slideVariants = {
  initial: {
    scaleY: 1,
  },
  enter: {
    scaleY: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    scaleY: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const PageTransition = ({ children }) => {
  return (
    <>
      {/* Page content with fade animation */}
      <motion.div
        initial="initial"
        animate="enter"
        exit="exit"
        variants={pageVariants}
      >
        {children}
      </motion.div>

      {/* Primary slide overlay - Cinematic black wipe */}
      <motion.div
        className="fixed inset-0 bg-black z-[60] origin-top pointer-events-none"
        initial="initial"
        animate="enter"
        exit="exit"
        variants={slideVariants}
      />
      
      {/* Secondary overlay - Gradient effect */}
      <motion.div
        className="fixed inset-0 z-[59] origin-top pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, #111 0%, #000 50%, #111 100%)'
        }}
        initial={{ scaleY: 1 }}
        animate={{ 
          scaleY: 0,
          transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.05 }
        }}
        exit={{ 
          scaleY: 1,
          transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
        }}
      />

      {/* Tertiary overlay - Accent color hint */}
      <motion.div
        className="fixed inset-0 z-[58] origin-top pointer-events-none bg-gradient-to-b from-gray-900 via-gray-800 to-black"
        initial={{ scaleY: 1 }}
        animate={{ 
          scaleY: 0,
          transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }
        }}
        exit={{ 
          scaleY: 1,
          transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.05 }
        }}
      />
    </>
  );
};

// Animated text reveal for page titles
export const AnimatedTitle = ({ children, className = '', delay = 0 }) => {
  return (
    <div className="overflow-hidden">
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ 
          duration: 0.8, 
          delay,
          ease: [0.22, 1, 0.36, 1] 
        }}
        className={className}
      >
        {children}
      </motion.div>
    </div>
  );
};

// Stagger container for child animations
export const StaggerContainer = ({ children, className = '', delay = 0 }) => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
            delayChildren: delay,
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Fade up animation for individual items
export const FadeUp = ({ children, className = '', delay = 0 }) => {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: {
            duration: 0.6,
            delay,
            ease: [0.22, 1, 0.36, 1],
          }
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default PageTransition;
